import type { FC } from "react";

import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import Button from "@mui/material/Button";
import { Controller } from "react-hook-form";
import {
  MdAdd,
  MdOutlineRemove,
  MdCheck,
  MdDeleteForever,
} from "react-icons/md";
import {
  ActionsDropdown,
  ThemesDropdown,
  CountriesDropdown,
  DatePicker,
  useLanguageTranslator,
} from "../../components";

import { useCallback } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import dayjs from "dayjs";

export type CareerActionsFormProps = {
  name: string;
  disabled?: boolean;
};

const CareerActionsForm: FC<CareerActionsFormProps> = (props) => {
  const { name, disabled } = props;

  const form = useFormContext();
  const { translator } = useLanguageTranslator();
  const { fields, append, remove, replace, update } = useFieldArray({
    name,
    keyName: "_id",
  });

  const handleAddAction = useCallback(() => {
    append({
      actionId: "",
      themeId: "",
      countryId: "",
      actionDate: "",
    });
  }, [append]);

  const handleRemoveAction = useCallback(
    (index: number) => () => {
      const fieldName = `${name}.${index}`;
      const fieldData = form.getValues(fieldName);

      // If action has an `id`. toggle `_destroy` key.
      if (fieldData.hasOwnProperty("id")) {
        update(index, { ...fieldData, _destroy: !fieldData._destroy });
      }
      // Else remove.
      else remove(index);
    },
    [fields, form, name, update, remove]
  );

  return (
    <Stack gap={2}>
      {fields?.map((data: any, i: any) => (
        <Stack key={data._id} direction={{ md: "row" }} gap={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <Controller
                name={`${name}.${i}.actionId`}
                defaultValue={""}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    disabled={disabled || data._destroy}
                    error={Boolean(error)}
                    fullWidth
                  >
                    <FormLabel>
                      {translator("dashboardTableHead.Action")}
                    </FormLabel>
                    <ActionsDropdown {...field} />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Controller
                name={`${name}.${i}.themeId`}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    disabled={disabled || data._destroy}
                    error={Boolean(error)}
                    fullWidth
                  >
                    <FormLabel>{translator("Sector")}</FormLabel>
                    <ThemesDropdown {...field} />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Controller
                name={`${name}.${i}.countryId`}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    disabled={disabled || data._destroy}
                    error={Boolean(error)}
                    fullWidth
                  >
                    <FormLabel>{translator("Country")}</FormLabel>
                    <CountriesDropdown {...field} />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Controller
                name={`${name}.${i}.actionDate`}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    disabled={disabled || data._destroy}
                    error={Boolean(error)}
                    fullWidth
                  >
                    <FormLabel>{translator("generic.Date")}</FormLabel>
                    <DatePicker
                      selected={field?.value}
                      disabled={disabled || data._destroy}
                      name="actionDate"
                      onSelect={(date: any) => {
                        if (date) {
                          const formattedDate =
                            dayjs(date).format("YYYY-MM-DD");
                          field.onChange(formattedDate);
                        }
                      }}
                    />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>

          <div style={{ flexShrink: 0 }}>
            <FormLabel>&nbsp;</FormLabel>
            <Button
              fullWidth
              sx={{
                backgroundColor: data._destroy ? "#00AAB0" : "white",
                color: data._destroy ? "white" : "#FA6262",
                borderColor: data?._destroy ? "#FA6262" : "#00AAB0",
                border: "1px solid",
                outline: "#00AAB0",
                "&:hover": {
                  backgroundColor: data?._destroy ? "#00AAB0" : "",
                },
                "&.Mui-focused": {
                  backgroundColor: data?._destroy ? "#00AAB0" : "",
                },
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: data?._destroy ? "#00AAB0" : "",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: data?._destroy ? "#00AAB0" : "",
                  },
                },
              }}
              startIcon={
                data._destroy ? (
                  <MdCheck size={24} />
                ) : data.id ? (
                  <MdDeleteForever size={24} />
                ) : (
                  <MdOutlineRemove size={24} />
                )
              }
              disabled={disabled}
              onClick={handleRemoveAction(i)}
              size="large"
            >
              {data._destroy
                ? "Keep"
                : data.id
                ? translator("generic.DELETE")
                : translator("generic.Remove")}
            </Button>
          </div>
        </Stack>
      ))}
      <div>
        <Button
          color="success"
          variant="outlined"
          startIcon={<MdAdd size={24} />}
          disabled={disabled}
          onClick={handleAddAction}
          size="large"
        >
          {translator("Add")} {translator("dashboardTableHead.Action")}
        </Button>
      </div>
    </Stack>
  );
};

export default CareerActionsForm;
